import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { FC } from "react";

import { WhiteIcon } from "./WhiteIcon";
import { SemanticIcon } from "./SemanticIcon";

import {
  AllIconName,
  IconName,
  IconType,
  IconVariant,
  SemanticIconName,
} from "./types";
import { SizedIcon } from "./SizedIcon";
import { BaseIcon } from "./BaseIcon";
import { BaseComponentProps } from "../types";
import { WhiteIconVariant, IconColor } from "./types";
import { IconSize } from "../Icon";

/**
 * Parse icon color to hex or css variable from tokens
 * @param iconColor The color to parse in hex `#ffffff` or token color `primary-400`
 * @returns Actual hex `#ffffff` or css variable `var(--color-primary-400)`
 */
const interpolateIconColor = (iconColor: IconColor): string => {
  return iconColor.indexOf("#") === 0 ? iconColor : `var(--color-${iconColor})`;
};

type StyledIconProps = {
  $variant: IconVariant;
  $plain?: boolean;
  $iconColor?: IconColor;
};

const StyledIcon = styled(SizedIcon)<StyledIconProps>`
  ${({ $variant, $plain, $iconColor }): FlattenSimpleInterpolation => {
    let iconColor: IconColor;
    let hoverColor: IconColor | null = null;

    switch ($variant) {
      case "DISABLED":
        iconColor = "primary-200";
        break;
      case "DANGER":
        iconColor = "error-600";
        hoverColor = "error-500";
        break;
      case "ACTIVE":
        iconColor = "secondary-800";
        break;
      case "INACTIVE":
        iconColor = "primary-400";
        break;
      case "DEFAULT":
      default:
        iconColor = $iconColor || "primary-800";
        hoverColor = "secondary-600";
    }

    return css`
      svg {
        --main-color: ${interpolateIconColor(iconColor)};
        ${$plain || !hoverColor
          ? ""
          : `:hover {
          --main-color: var(--color-${hoverColor});
        }`}
      }
    `;
  }}
`;

type IconProps = BaseComponentProps<
  "svg",
  {
    /** The icon that will be rendered. */
    iconName: AllIconName;

    /** The size of icon that will be rendered. */
    iconSize?: IconSize;

    /** The variation of icon that will be rendered. */
    variant?: IconVariant;

    /** This variable disables the hover effect on default. */
    plain?: boolean;

    /** The type of icon rendered. */
    type?: IconType;

    /** The color of icon rendered, only for variant `DEFAULT`. */
    iconColor?: IconColor;
  }
>;

export const Icon: FC<IconProps> = ({
  iconSize = "regular",
  variant = "DEFAULT",
  type = "default",
  className,
  plain,
  iconColor,
  ...props
}) => {
  switch (type) {
    case "semantic":
      return (
        <SemanticIcon
          {...props}
          iconName={props.iconName as SemanticIconName}
          iconSize={iconSize}
          className={className}
        />
      );
    case "white":
      return (
        <WhiteIcon
          {...props}
          iconName={props.iconName as IconName}
          iconSize={iconSize}
          variant={variant as WhiteIconVariant}
          plain={plain}
          className={className}
        />
      );
    default:
      return (
        <StyledIcon
          className={className}
          $iconSize={iconSize}
          $variant={variant}
          $plain={plain}
          $iconColor={iconColor}
        >
          <BaseIcon disabled={variant === "DISABLED"} {...props} />
        </StyledIcon>
      );
  }
};

// TODO: Remove this component as soon as possible. Use the prop `iconSize` instead.

export const SmallIcon: FC<IconProps> = ({
  iconSize = "small",
  variant = "DEFAULT",
  className,
  plain,
  iconColor,
  ...props
}) => (
  <StyledIcon
    className={className}
    $iconSize={iconSize}
    $variant={variant}
    $plain={plain}
    $iconColor={iconColor}
  >
    <BaseIcon disabled={variant === "DISABLED"} {...props} />
  </StyledIcon>
);
